/**
 * Bolt Checkout — all-in-one snippet (copy-paste)
 * ===============================================
 * Renders the Bolt checkout button, launches checkout, AND emits analytics
 * events to the marketing tools you already have on the page (Klaviyo,
 * Northbeam, Google Analytics / GTM) — all from one snippet.
 * 
 * Prerequisites (you do these already):
 *   1. Bolt's track.js + connect.js in your <head> with your publishable key.
 *
 * Custom checkout button (e.g. Kuru's own "Checkout" button)
 * ------------------------------------------------------------------------
 * Bolt's `<object>` button above keeps working as-is. In addition, this
 * snippet exposes `window.BoltOpenCheckout()` — call it from any of your
 * own elements (onclick, addEventListener, framework handler, etc.) to open
 * the exact same Bolt checkout modal programmatically:
 *
 *   <button onclick="window.BoltOpenCheckout()">Checkout</button>
 *
 * It is safe to call before Bolt has finished configuring — it will log a
 * console error and no-op rather than throw if the checkout isn't ready yet.
 *
 */
(function() {
	"use strict";

	// ==========================================================================
	// CONFIG — EDIT THIS SECTION. (No need to change anything below it.)
	// ==========================================================================

	const BOLT_BUTTON_SELECTOR = '[data-tid*="bolt-checkout-button"]';
	const DYNAMIC_ELEMENTS_SELECTOR = "#shopify-section-kf-cart-drawer";
	const CART_TOKEN_COOKIE_NAME = "cart";

	// --- Optional. Pre-populate the shopper's information. -------------------
	const hints = {
		// prefill: {
		//   firstName: "Bolt",
		//   lastName: "User",
		//   email: "email@example.com",
		//   phone: "1112223333",
		//   addressLine1: "1235 Howard St",
		//   addressLine2: "Unit D",
		//   city: "San Francisco",
		//   state: "California",
		//   zip: "94103",
		//   country: "US", // ISO Alpha-2
		// },
	};

	function centsToDollars(cents) {
		return cents === undefined || cents === null ? undefined : cents / 100;
	}

	async function fetchShopifyCart() {
		const response = await fetch(window.Shopify.routes.root + "cart.js");
		if (!response.ok) {
			throw new Error("Error fetching Shopify cart: " + (await response.text()));
		}
		return response.json();
	}

	async function getCartToken() {
		let encodedToken;
		if (window.cookieStore) {
			const cookie = await window.cookieStore.get(CART_TOKEN_COOKIE_NAME);
			encodedToken = cookie && cookie.value;
		} else {
			const row = document.cookie.split("; ").find(function(r) {
				return r.indexOf(CART_TOKEN_COOKIE_NAME + "=") === 0;
			});
			encodedToken = row ? row.split("=")[1] : undefined;
		}
		if (encodedToken) {
			return decodeURIComponent(encodedToken);
		}
		const cart = await fetchShopifyCart();
		return cart.token;
	}

	async function clearCart() {
		if (window.cookieStore) {
			await window.cookieStore.delete(CART_TOKEN_COOKIE_NAME);
			return;
		}
		document.cookie = CART_TOKEN_COOKIE_NAME + "=; path=/; expires=" + new Date(0).toUTCString();
	}

	function getPdpInfo() {
		// Favor the quick add modal ("Choose option") first.
		const form =
			document.querySelector("quick-add-modal[open] form[data-type=add-to-cart-form]") ||
			document.querySelector("form[data-type=add-to-cart-form]");
		if (form === null) {
			throw new Error("PDP Info form not found");
		}
		const formData = new FormData(form);
		return {
			productId: formData.get("product-id"),
			variantId: formData.get("id"),
			quantity: Number(formData.get("quantity") || 1),
		};
	}

	function debounce(func, delay) {
		let timeout;
		return function() {
			const args = [].slice.call(arguments);
			clearTimeout(timeout);
			timeout = setTimeout(function() {
				func.apply(null, args);
			}, delay);
		};
	}

	function mapShopifyCartToKlaviyoCheckout(cart) {
		const lineItems = (cart.items || []).map(function(item) {
			return {
				key: item.key,
				sku: item.sku,
				title: item.title,
				taxable: item.taxable,
				gift_card: item.gift_card,
				line_price_set: {
					shop_money: { amount: centsToDollars(item.price) },
					presentment_money: { amount: item.presentment_price },
				},
				grams: item.grams,
				line_price: centsToDollars(item.line_price),
				product_id: item.product_id,
				variant_id: item.variant_id,
				variant_price: centsToDollars(item.price),
				variant_title: item.variant_title || undefined,
				vendor: item.vendor,
				quantity: item.quantity,
				price: centsToDollars(item.price),
				properties: item.properties,
				applied_discounts: item.discounts,
				requires_shipping: item.requires_shipping,
				product: {
					handle: item.handle,
					id: item.product_id,
					title: item.product_title,
					product_type: item.product_type,
					vendor: item.vendor,
					images: item.featured_image
						? [Object.assign({ src: item.featured_image.url }, item.featured_image)]
						: [],
				},
			};
		});

		return {
			"Source Name": "Bolt Managed Checkout",
			"$value": centsToDollars(cart.total_price),
			"Customer Locale": navigator.language,
			"Collections": [],
			"Total Discounts": centsToDollars(cart.total_discount),
			"Discount Codes": (cart.discount_codes || [])
				.filter(function(d) { return d.applicable; })
				.map(function(d) { return d.code; }),
			"Item Count": cart.item_count,
			"Items": (cart.items || []).map(function(item) { return item.title; }),
			"extra": {
				"referring_site": document.referrer,
				// Only forward the public part of the token; the key is a secret.
				"token": cart.token.split("?")[0],
				"line_items": lineItems,
				"presentment_currency": cart.currency,
				"note_attributes": Object.entries(cart.attributes || {}).map(function(entry) {
					return { name: entry[0], value: entry[1] };
				}),
			},
		};
	}

	function configureDynamicElements(selector, builtCallbacks) {
		const dynamicElements = document.querySelectorAll(selector);
		log("Watching for dynamic changes on", dynamicElements);

		const debouncedConfigure = debounce(async function() {
			log("Bolt button might have changed, configuring");
			try {
				const cartToken = await getCartToken();
				const result = window.BoltCheckout.configure({ id: cartToken }, hints || {}, builtCallbacks);
				setBoltCheckoutHandle(result);
			} catch (e) {
				console.error(LOG_PREFIX, "Error fetching Shopify token:", e);
			}
		}, 50);

		// Watch parents — the observer does not fire for removal of the element itself.
		const targets = [].map.call(dynamicElements, function(elem) {
			return elem.parentElement || document;
		});
		new Set(targets).forEach(function(elem) {
			const observer = new MutationObserver(function(mutations) {
				mutations.forEach(function(mutation) {
					if (mutation.type !== "childList") {
						return;
					}
					if (mutation.target instanceof Element && mutation.target.querySelector(BOLT_BUTTON_SELECTOR)) {
						debouncedConfigure();
					}
				});
			});
			observer.observe(elem, { childList: true, subtree: true });
		});
	}

	// --- Optional. Your own checkout lifecycle hooks. ------------------------
	// These run IN ADDITION to the analytics events below. The Shopify cart is
	// cleared automatically on success. If you define `success`, call `callback`.
	const callbacks = {
		success: async function(transaction, callback) {
			try {
				await clearCart();
			} catch (e) {
				console.error(LOG_PREFIX, "Error clearing cart:", e);
			}
			callback();
		},

		// check: function () { return true; },   // must return a boolean
		// onCheckoutStart: function () {},
		// onEmailEnter: function (email) {},
		// onShippingDetailsComplete: function (address) {},
		// onShippingOptionsComplete: function () {},
		// onPaymentSubmit: function () {},
		// close: function () {},
	};

	// --- Analytics. Toggle the tools you use. --------------------------------
	// Each is also skipped automatically if the tool is not detected on the page.
	const analytics = {
		klaviyo: true,
		northbeam: true,
		google: true, // gtag() and/or window.dataLayer
	};

	// Rich Klaviyo payload, plus generic value/currency/items for Google.
	async function getCheckoutData() {
		try {
			const cart = await fetchShopifyCart();
			const data = mapShopifyCartToKlaviyoCheckout(cart);
			data.$value = centsToDollars(cart.total_price);
			data.currency = cart.currency;
			data.Items = (cart.items || []).map(function(item) {
				return {
					id: item.sku || item.product_id,
					name: item.title,
					price: centsToDollars(item.price),
					quantity: item.quantity,
				};
			});
			return data;
		} catch (e) {
			console.error(LOG_PREFIX, "Error fetching checkout data:", e);
			return null;
		}
	}

	// Set to true to log every checkout event + emitted analytics to the console.
	var debug = false;

	// ==========================================================================
	// END CONFIG — you do not need to edit anything below this line.
	// ==========================================================================

	var LOG_PREFIX = "[Bolt Checkout]";

	function log() {
		if (debug) {
			console.debug.apply(console, [LOG_PREFIX].concat([].slice.call(arguments)));
		}
	}

	// Run something best-effort. A failing hook (analytics or merchant) must
	// never block or break checkout.
	function safely(name, fn, args) {
		if (typeof fn !== "function") {
			return undefined;
		}
		try {
			return fn.apply(null, args || []);
		} catch (e) {
			console.error(LOG_PREFIX, "Error in '" + name + "':", e);
			return undefined;
		}
	}

	async function getData() {
		return (await safely("getCheckoutData", getCheckoutData)) || null;
	}

	// --------------------------------------------------------------------------
	// Analytics emitters — each is a no-op if the tool is disabled or absent.
	// --------------------------------------------------------------------------
	function klaviyoTrack(event, properties) {
		if (analytics.klaviyo && window.klaviyo && typeof window.klaviyo.track === "function") {
			log("klaviyo track", event, properties);
			safely("klaviyo.track", function() { window.klaviyo.track(event, properties || {}); });
		}
	}

	function klaviyoIdentify(properties) {
		if (analytics.klaviyo && window.klaviyo && typeof window.klaviyo.identify === "function") {
			log("klaviyo identify", properties);
			safely("klaviyo.identify", function() { window.klaviyo.identify(properties || {}); });
		}
	}

	function northbeamIdentifyEmail(email) {
		if (analytics.northbeam && window.Northbeam && typeof window.Northbeam.identify === "function") {
			log("northbeam identify", email);
			safely("Northbeam.identify", function() { window.Northbeam.identify("email", email); });
		}
	}

	function googleEvent(eventName, params) {
		if (!analytics.google) {
			return;
		}
		if (typeof window.gtag === "function") {
			log("gtag", eventName, params);
			safely("gtag", function() { window.gtag("event", eventName, params || {}); });
		} else if (Array.isArray(window.dataLayer)) {
			log("dataLayer", eventName, params);
			safely("dataLayer", function() { window.dataLayer.push(Object.assign({ event: eventName }, params || {})); });
		}
	}

	// Map our generic checkout data into a Google ecommerce payload.
	function toGoogleEcommerce(data) {
		if (!data) {
			return {};
		}
		return {
			currency: data.currency,
			value: data.value,
			items: (data.items || []).map(function(item) {
				return {
					item_id: item.id || item.sku,
					item_name: item.name || item.title,
					price: item.price,
					quantity: item.quantity,
				};
			}),
		};
	}

	// Built-in analytics for each lifecycle event (runs before the merchant's
	// own hook of the same name).
	var emit = {
		onCheckoutStart: async function() {
			const data = await getData();
			// KURU: notify the Convert bridge (snippets/kf-bolt-convert-tracking) via a
			// DOM event from this proven-reliable callback, instead of wrapping configure.
			try { document.dispatchEvent(new CustomEvent("kf:bolt:checkout_start", { detail: data })); } catch (e) {}
			klaviyoTrack("Checkout Started", data || {});
			googleEvent("begin_checkout", toGoogleEcommerce(data));
		},
		onEmailEnter: function(email) {
			if (!email) {
				return;
			}
			klaviyoIdentify({ email: email });
			northbeamIdentifyEmail(email);
		},
		onShippingDetailsComplete: function(address) {
			if (address) {
				klaviyoIdentify(address);
			}
		},
		onPaymentSubmit: async function() {
			googleEvent("add_payment_info", toGoogleEcommerce(await getData()));
		},
		onSuccess: async function(transaction) {
			// KURU: notify the Convert bridge with the transaction (has amount +
			// reference) synchronously, before Bolt cedes control and redirects.
			try { document.dispatchEvent(new CustomEvent("kf:bolt:success", { detail: { transaction: transaction } })); } catch (e) {}
			googleEvent("purchase", Object.assign(
				{ transaction_id: transaction && (transaction.reference || transaction.id) },
				toGoogleEcommerce(await getData())
			));
		},
	};

	// --------------------------------------------------------------------------
	// Build the callbacks object passed to Bolt: analytics first, then the
	// merchant's own hook. The two contractual callbacks are handled explicitly:
	//   - `check`   must return a boolean (defaults to allowing checkout)
	//   - `success` must invoke its `callback` (guaranteed even on error)
	// --------------------------------------------------------------------------

	// Compose an analytics emitter with the merchant's optional hook into one
	// callback. Logs and is crash-isolated.
	function combine(name, emitFn) {
		var merchantFn = callbacks ? callbacks[name] : undefined;
		return function() {
			var args = [].slice.call(arguments);
			log(name, args);
			safely(name + " (analytics)", emitFn, args);
			safely(name + " (merchant)", merchantFn, args);
		};
	}

	function buildCallbacks() {
		var merchant = callbacks || {};

		return {
			check: function() {
				log("check");
				if (typeof merchant.check === "function") {
					return safely("check", merchant.check, [].slice.call(arguments)) !== false;
				}
				return true;
			},

			onCheckoutStart: combine("onCheckoutStart", emit.onCheckoutStart),
			onEmailEnter: combine("onEmailEnter", emit.onEmailEnter),
			onShippingDetailsComplete: combine("onShippingDetailsComplete", emit.onShippingDetailsComplete),
			onShippingOptionsComplete: combine("onShippingOptionsComplete", null),
			onPaymentSubmit: combine("onPaymentSubmit", emit.onPaymentSubmit),
			close: combine("close", null),

			success: function(transaction, callback) {
				log("success", transaction);
				safely("success (analytics)", emit.onSuccess, [transaction]);

				var called = false;
				var safeCallback = function() {
					if (called) {
						return;
					}
					called = true;
					if (typeof callback === "function") {
						callback();
					}
				};

				if (typeof merchant.success !== "function") {
					safeCallback();
					return;
				}

				try {
					var maybePromise = merchant.success(transaction, safeCallback);
					// Support async success handlers that return a promise.
					if (maybePromise && typeof maybePromise.then === "function") {
						maybePromise.then(safeCallback, function(e) {
							console.error(LOG_PREFIX, "Error in async 'success' callback:", e);
							safeCallback();
						});
					}
				} catch (e) {
					console.error(LOG_PREFIX, "Error in 'success' callback:", e);
					safeCallback();
				}
			},
		};
	}

	function isBoltReady(method) {
		if (!window.BoltCheckout || typeof window.BoltCheckout[method] !== "function") {
			console.error(
				LOG_PREFIX,
				"BoltCheckout." + method + " is not available. Ensure Bolt's connect.js is loaded before this snippet."
			);
			return false;
		}
		return true;
	}

	// --------------------------------------------------------------------------
	// Custom checkout button support. `window.BoltOpenCheckout()` opens the
	// exact same checkout modal Bolt's own <object> button would, using
	// whichever configure() call ran most recently (cart or PDP).
	// --------------------------------------------------------------------------
	var boltCheckoutHandle = null;

	function setBoltCheckoutHandle(handle) {
		boltCheckoutHandle = handle;
	}

	window.BoltOpenCheckout = function() {
		if (boltCheckoutHandle && typeof boltCheckoutHandle.open === "function") {
			log("BoltOpenCheckout: opening");
			safely("BoltOpenCheckout", function() { boltCheckoutHandle.open(); });
		} else {
			console.error(LOG_PREFIX, "BoltOpenCheckout: checkout is not configured yet.");
		}
	};

	async function setup() {
		if (!window.Shopify || !window.Shopify.routes) {
			console.error(LOG_PREFIX, "window.Shopify not found.");
			return;
		}
		if (!isBoltReady("configure")) {
			return;
		}

		const builtCallbacks = buildCallbacks();

		try {
			const cartToken = await getCartToken();
			const result = window.BoltCheckout.configure({ id: cartToken }, hints || {}, builtCallbacks);
			setBoltCheckoutHandle(result);
			if (!result || !result.hasBoltButton) {
				console.warn(LOG_PREFIX, "No Bolt buttons were found on the page.");
			}
		} catch (e) {
			console.error(LOG_PREFIX, "Error fetching Shopify cart token:", e);
		}

		if (isBoltReady("configureProductCheckout")) {
			try {
				const pdpInfo = getPdpInfo();
				const productCart = {
					currency: (window.Shopify.currency && window.Shopify.currency.active) || "USD",
					items: [{ price: 0, quantity: pdpInfo.quantity, merchantVariantID: pdpInfo.variantId }],
				};
				const productResult = window.BoltCheckout.configureProductCheckout(productCart, hints || {}, builtCallbacks);
				setBoltCheckoutHandle(productResult);

				document.querySelectorAll('input[name="quantity"]').forEach(function(elem) {
					elem.addEventListener("change", function() {
						productCart.items[0].quantity = getPdpInfo().quantity;
						const updatedResult = window.BoltCheckout.configureProductCheckout(productCart, hints || {}, builtCallbacks);
						setBoltCheckoutHandle(updatedResult);
					});
				});
			} catch (e) {
				log("Not on a product page");
			}
		}

		if (DYNAMIC_ELEMENTS_SELECTOR) {
			configureDynamicElements(DYNAMIC_ELEMENTS_SELECTOR, builtCallbacks);
		}
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", setup);
	} else {
		setup();
	}
})();
